import { createContext, useMemo, useEffect, useState } from "react";
import { Chakra } from "@airhelp/react-ui-components";
import type { StickyBottomBarContextProps, StickyBottomBarContextProviderProps } from "./StickyBottomBarContext.d";

const { useDisclosure } = Chakra;

export const StickyBottomBarContext = createContext<StickyBottomBarContextProps | undefined>(undefined);

export const StickyBottomBarContextProvider = ({ threshold = 0, children }: StickyBottomBarContextProviderProps) => {
  const { isOpen, onOpen: showBottomBar, onClose: hideBottomBar } = useDisclosure();
  const [firstTriggerElement, setFirstTriggerElement] = useState<Element>(null);
  const [secondTriggerElement, setSecondTriggerElement] = useState<Element>(null);

  useEffect(() => {
    const callBack = (entries: IntersectionObserverEntry[]) =>
      entries.every(({ target, isIntersecting }) => (target && isIntersecting ? hideBottomBar() : showBottomBar()));

    const observer = new IntersectionObserver(callBack, { threshold });

    if (firstTriggerElement) {
      observer.observe(firstTriggerElement);
    }

    if (secondTriggerElement) {
      observer.observe(secondTriggerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [firstTriggerElement, hideBottomBar, secondTriggerElement, showBottomBar, threshold]);

  const value = useMemo(
    () => ({
      isOpen,
      setFirstTriggerElement,
      setSecondTriggerElement,
    }),
    [isOpen, setFirstTriggerElement, setSecondTriggerElement]
  );

  return <StickyBottomBarContext.Provider value={value}>{children}</StickyBottomBarContext.Provider>;
};
